/* eslint-disable no-underscore-dangle */
import L, {
  LatLng,
  MarkerCluster
} from 'leaflet';
import Graph from 'graphology';
import { bfsFromNode } from 'graphology-traversal';
import {
  Connection,
  NetworkElementProps,
  NetworkTopologyClusterElementsEnum,
  NetworkTopologyElement,
  NetworkTopologyElementsEnum,
  NetworkTopologyMapData
} from 'data/network-topology/network-topology.types';
import { getClusterStatus2 } from './adaptConnectorCollor';
import getTooltipData, { TooltipRow } from './tooltipData';

export type DistinctDestination = {
  [id: string]: {
    sourceLeafletId: string;
    sourceId: string,
    sourceType: NetworkTopologyClusterElementsEnum | NetworkTopologyElementsEnum;
    destinationType: NetworkTopologyClusterElementsEnum | NetworkTopologyElementsEnum;
    destinationLocation: any;
    statuses: string[];
  };
}

export type NeNodeAttr = {
  markerType: NetworkTopologyClusterElementsEnum | NetworkTopologyElementsEnum,
  isCluster: boolean,
  childCount?: number,
  childNeData?: NetworkElementProps[],
  location: LatLng,
  neDetails?: NetworkElementProps,
};

export type NeEdge = {
  edgeKey: string;
  sourceLeafletId: string,
  sourceType: NetworkTopologyClusterElementsEnum | NetworkTopologyElementsEnum,
  sourceLocation: LatLng,
  sourceId: string,
  destinationId: string,
  destinationLeafletId: string,
  destinationType: NetworkTopologyClusterElementsEnum | NetworkTopologyElementsEnum,
  destinationLocation: LatLng | undefined,
  statuses?: string[],
  status: string,
  tooltipData?: TooltipRow[]
}

export type MarkerSummary = {
  markerType: NetworkTopologyClusterElementsEnum | NetworkTopologyElementsEnum
  isCluster: boolean;
  childCount?: number;
  neDetails?: NetworkTopologyElement;
  childNeData?: NetworkTopologyElement[];
  location: LatLng;
}

export default class MapHelper {
  afClusters: any;
  cfClusters: any;
  xcipioClusters: any;
  ccpagClusters: any;
  mdf2Clusters: any;
  mdf3Clusters: any;
  admfClusters: any;
  graph = new Graph({ multi: true, allowSelfLoops: false, type: 'directed' });

  /*
    Helper Maps
  */
  afIdToLeafletIdMap: { [neId: string]: { leafletId: string, location: LatLng}} = {};
  cfIdToLeafletIdMap: { [neId: string]: { leafletId: string, location: LatLng}} = {};
  xcipioIdToLeafletIdMap: { [neId: string]: { leafletId: string, location: LatLng}} = {};
  ccpagIdToLeafletIdMap: { [neId: string]: { leafletId: string, location: LatLng}} = {};
  mdf2IdToLeafletIdMap: { [neId: string]: { leafletId: string, location: LatLng}} = {};
  mdf3IdToLeafletIdMap: { [neId: string]: { leafletId: string, location: LatLng}} = {};
  admfIdToLeafletIdMap: { [neId: string]: { leafletId: string, location: LatLng}} = {};
  afLeafletIdToMarkerMap: { [leafletId: string]: MarkerSummary } = {};
  cfLeafletIdToMarkerMap: { [leafletId: string]: MarkerSummary } = {};
  xcipioLeafletIdToMarkerMap: { [leafletId: string]: MarkerSummary } = {};
  ccpagLeafletIdToMarkerMap: { [leafletId: string]: MarkerSummary } = {};
  mdf2LeafletIdToMarkerMap: { [leafletId: string]: MarkerSummary } = {};
  mdf3LeafletIdToMarkerMap: { [leafletId: string]: MarkerSummary } = {};
  admfLeafletIdToMarkerMap: { [leafletId: string]: MarkerSummary } = {};

  constructor(
    private afClusterGroup: any,
    private cfClusterGroup: L.MarkerClusterGroup,
    private xcipioClusterGroup: L.MarkerClusterGroup,
    private ccpagsClusterGroup: L.MarkerClusterGroup,
    private mdf2ClusterGroup: L.MarkerClusterGroup,
    private mdf3ClusterGroup: L.MarkerClusterGroup,
    private admfClusterGroup: L.MarkerClusterGroup,
    private mapData: NetworkTopologyMapData,
    private map: L.Map
  ) {
    this.initializeClusters();
    this.buildLookups();
  }

  initializeClusters() {
    this.afClusters = (this.afClusterGroup as any)._featureGroup._layers;
    if (this.afClusters && Object.entries(this.afClusters).length === 0) {
      this.afClusters = (this.afClusterGroup as L.MarkerClusterGroup).getLayers();
    }
    this.cfClusters = (this.cfClusterGroup as any)._featureGroup._layers;
    if (this.cfClusters && Object.entries(this.cfClusters).length === 0) {
      this.cfClusters = (this.cfClusterGroup as L.MarkerClusterGroup).getLayers();
    }
    this.xcipioClusters = (this.xcipioClusterGroup as any)._featureGroup._layers;
    if (
      this.xcipioClusters &&
      Object.entries(this.xcipioClusters).length === 0
    ) {
      this.xcipioClusters = (this.xcipioClusterGroup as L.MarkerClusterGroup).getLayers();
    }
    this.ccpagClusters = (this.ccpagsClusterGroup as any)._featureGroup._layers;
    if (this.ccpagClusters && Object.entries(this.ccpagClusters).length === 0) {
      this.ccpagClusters = (this.ccpagsClusterGroup as L.MarkerClusterGroup).getLayers();
    }
    this.mdf2Clusters = (this.mdf2ClusterGroup as any)._featureGroup._layers;
    if (this.mdf2Clusters && Object.entries(this.mdf2Clusters).length === 0) {
      this.mdf2Clusters = (this.mdf2ClusterGroup as L.MarkerClusterGroup).getLayers();
    }
    this.mdf3Clusters = (this.mdf3ClusterGroup as any)._featureGroup._layers;
    if (this.mdf3Clusters && Object.entries(this.mdf3Clusters).length === 0) {
      this.mdf3Clusters = (this.mdf3ClusterGroup as L.MarkerClusterGroup).getLayers();
    }
    this.admfClusters = (this.admfClusterGroup as any)._featureGroup._layers;
    if (this.admfClusters && Object.entries(this.admfClusters).length === 0) {
      this.admfClusters = (this.admfClusterGroup as L.MarkerClusterGroup).getLayers();
    }
    this.handleCollisions();
  }

  buildLookups() {
    this.afIdToLeafletIdMap = this.getNeIdToLeafletIdAndLocationMap(this.afClusters);
    this.xcipioIdToLeafletIdMap = this.getNeIdToLeafletIdAndLocationMap(this.xcipioClusters);
    this.cfIdToLeafletIdMap = this.getNeIdToLeafletIdAndLocationMap(this.cfClusters);
    this.ccpagIdToLeafletIdMap = this.getNeIdToLeafletIdAndLocationMap(this.ccpagClusters);
    this.mdf2IdToLeafletIdMap = this.getNeIdToLeafletIdAndLocationMap(this.mdf2Clusters);
    this.mdf3IdToLeafletIdMap = this.getNeIdToLeafletIdAndLocationMap(this.mdf3Clusters);
    this.admfIdToLeafletIdMap = this.getNeIdToLeafletIdAndLocationMap(this.admfClusters);
    this.afLeafletIdToMarkerMap = this.getLeafletIdToMarkerMap(this.afClusters, NetworkTopologyClusterElementsEnum.AccessFunctionCluster, NetworkTopologyElementsEnum.AccessFunction);
    this.cfLeafletIdToMarkerMap = this.getLeafletIdToMarkerMap(this.cfClusters, NetworkTopologyClusterElementsEnum.CollectionFunctionCluster, NetworkTopologyElementsEnum.CollectionFunction);
    this.xcipioLeafletIdToMarkerMap = this.getLeafletIdToMarkerMap(this.xcipioClusters, NetworkTopologyClusterElementsEnum.XcipioCluster, NetworkTopologyElementsEnum.Xcipio);
    this.ccpagLeafletIdToMarkerMap = this.getLeafletIdToMarkerMap(this.ccpagClusters, NetworkTopologyClusterElementsEnum.CCPAGCluster, NetworkTopologyElementsEnum.CCPAG);
    this.mdf2LeafletIdToMarkerMap = this.getLeafletIdToMarkerMap(this.mdf2Clusters, NetworkTopologyClusterElementsEnum.MDF2Cluster, NetworkTopologyElementsEnum.MDF2);
    this.mdf3LeafletIdToMarkerMap = this.getLeafletIdToMarkerMap(this.mdf3Clusters, NetworkTopologyClusterElementsEnum.MDF3Cluster, NetworkTopologyElementsEnum.MDF3);
    this.admfLeafletIdToMarkerMap = this.getLeafletIdToMarkerMap(this.admfClusters, NetworkTopologyClusterElementsEnum.ADMFCluster, NetworkTopologyElementsEnum.ADMF);
  }

  getNeIdToLeafletIdAndLocationMap(clusters: any) {
    const ret: { [id: string]: { leafletId: string, location: LatLng }} = {};
    Object.entries(clusters).forEach((c) => {
      if (c[1] instanceof L.MarkerCluster) {
        const cluster = c[1] as MarkerCluster;
        cluster.getAllChildMarkers().reduce((acc, marker) => {
          const details = (marker.options as any)['data-original'];
          acc[details.data.id] = { leafletId: (cluster as any)._leaflet_id, location: cluster.getLatLng() };
          return acc;
        }, ret as any);
      } else {
        const marker = c[1] as MarkerCluster;
        const details = (marker.options as any)['data-original'];
        ret[details.data.id] = { leafletId: (marker as any)._leaflet_id, location: marker.getLatLng() };
      }
    });
    return ret;
  }

  getLeafletIdToMarkerMap(clusters: any, clusterType: NetworkTopologyClusterElementsEnum, nonClusterType: NetworkTopologyElementsEnum) {
    const ret: { [id: string]: MarkerSummary } = {};
    Object.entries(clusters).forEach((c) => {
      if (c[1] instanceof L.MarkerCluster) {
        const cluster = c[1] as MarkerCluster;
        ret[(cluster as any)._leaflet_id] = {
          markerType: clusterType,
          isCluster: true,
          childCount: cluster.getChildCount(),
          childNeData: cluster.getAllChildMarkers().map((m) => (m.options as any)['data-original']),
          location: cluster.getLatLng()
        };
      } else {
        const marker = c[1] as MarkerCluster;
        ret[(marker as any)._leaflet_id] = {
          markerType: nonClusterType,
          isCluster: false,
          neDetails: (marker.options as any)['data-original'],
          location: marker.getLatLng()
        };
      }
    });
    return ret;
  }

  buildGraph(): Graph {
    this.addNodes(this.afClusters, NetworkTopologyClusterElementsEnum.AccessFunctionCluster, NetworkTopologyElementsEnum.AccessFunction);
    this.addNodes(this.cfClusters, NetworkTopologyClusterElementsEnum.CollectionFunctionCluster, NetworkTopologyElementsEnum.CollectionFunction);
    this.addNodes(this.xcipioClusters, NetworkTopologyClusterElementsEnum.XcipioCluster, NetworkTopologyElementsEnum.Xcipio);
    this.addNodes(this.ccpagClusters, NetworkTopologyClusterElementsEnum.CCPAGCluster, NetworkTopologyElementsEnum.CCPAG);
    this.addNodes(this.mdf2Clusters, NetworkTopologyClusterElementsEnum.MDF2Cluster, NetworkTopologyElementsEnum.MDF2);
    this.addNodes(this.mdf3Clusters, NetworkTopologyClusterElementsEnum.MDF3Cluster, NetworkTopologyElementsEnum.MDF3);
    this.addNodes(this.admfClusters, NetworkTopologyClusterElementsEnum.ADMFCluster, NetworkTopologyElementsEnum.ADMF);
    this.addEdges(this.afClusters, NetworkTopologyClusterElementsEnum.AccessFunctionCluster, NetworkTopologyElementsEnum.AccessFunction);
    this.addEdges(this.xcipioClusters, NetworkTopologyClusterElementsEnum.XcipioCluster, NetworkTopologyElementsEnum.Xcipio);
    this.addEdges(this.ccpagClusters, NetworkTopologyClusterElementsEnum.XcipioCluster, NetworkTopologyElementsEnum.Xcipio);
    this.addEdges(this.mdf2Clusters, NetworkTopologyClusterElementsEnum.MDF2Cluster, NetworkTopologyElementsEnum.MDF2);
    this.addEdges(this.mdf3Clusters, NetworkTopologyClusterElementsEnum.MDF3Cluster, NetworkTopologyElementsEnum.MDF3);
    this.addEdges(this.admfClusters, NetworkTopologyClusterElementsEnum.ADMFCluster, NetworkTopologyElementsEnum.ADMF);
    return this.graph;
  }

  addNodes(clusters: any, clusterType: NetworkTopologyClusterElementsEnum, singularType: NetworkTopologyElementsEnum) {
    Object.entries(clusters).forEach((c) => {
      if (c[1] instanceof L.MarkerCluster) {
        const cluster = c[1] as MarkerCluster;
        const leafletId = (cluster as any)._leaflet_id;
        this.graph.addNode(leafletId, {
          location: cluster.getLatLng(),
          markerType: clusterType,
          childNeData: this.getChildDataByLeafletId(leafletId),
          isCluster: true
        } as NeNodeAttr);
      } else {
        const marker = c[1] as L.Marker;
        this.graph.addNode(
          (marker as any)._leaflet_id, {
            neData: (marker.options as any)['data-original'],
            location: marker.getLatLng(),
            isCluster: false,
            markerType: singularType
          } as NeNodeAttr
        );
      }
    });
  }

  addEdges(clusters: any, clusterType: NetworkTopologyClusterElementsEnum, sourceType: NetworkTopologyElementsEnum) {
    let edges: NeEdge[] = [];
    Object.entries(clusters).forEach((c) => {
      if (c[1] instanceof L.MarkerCluster) {
        const distinctDestinations: DistinctDestination = {};
        const cluster = c[1] as MarkerCluster;
        cluster.getAllChildMarkers().reduce((acc, marker) => {
          const details = (marker.options as any)['data-original'];
          this.addDistinctDestinationsFromCluster(cluster, details, distinctDestinations, clusterType);
          return acc;
        }, distinctDestinations as any);
        const filteredDestinations = this.removeConnectionsToSelf(distinctDestinations);
        Object.entries(filteredDestinations).forEach((d) => {
          const edge = {
            edgeKey: '',
            sourceLeafletId: d[1].sourceLeafletId,
            sourceLocation: this.getMarkerLocationByNeId(d[1].sourceId),
            sourceId: d[1].sourceId,
            sourceType: d[1].sourceType,
            destinationId: d[0],
            destinationLeafletId: this.getLeafletId(d[0]),
            destinationType: d[1].destinationType,
            destinationLocation: d[1].destinationLocation,
            statuses: d[1].statuses,
            status: getClusterStatus2(d[1].statuses)
          };
          const tooltipData = getTooltipData(edge, this);
          edges.push({ ...edge, tooltipData });
        });
      } else {
        const marker = c[1] as L.Marker;
        const details = (marker.options as any)['data-original'];
        const markerEdges = this.getEdgesForNonClusteredMarker(details, (marker as any)._leaflet_id, sourceType);
        edges = [...edges, ...markerEdges]
      }
    });
    // Remove edges without a destination location, example when we filter by xcipio,
    // and that xcipio node has connections to elements not visible on map
    const validEdges = this.filterValidEdges(edges);
    validEdges.forEach((edge) => {
      const edgeKey = `${edge.sourceType}_${edge.sourceLeafletId}->${edge.destinationType}_${edge.destinationLeafletId}`;
      this.graph.addDirectedEdge(edge.sourceLeafletId, edge.destinationLeafletId, {
        ...edge
      });
    });
  }

  filterValidEdges(edges: NeEdge[]) {
    return edges.filter((e) => e.destinationLeafletId && e.destinationLocation && e.sourceLocation)
  }

dfs(node: any, visited: Set<any>, group: any, map: Map<any, any[]>) {
    // Add the node to the visited set and the current group.
    visited.add(node);
    group.push(node);
    if (!map.get(node)) {
      map.set(node, []);
    }
    // Traverse all adjacent nodes.
    map.get(node)?.forEach((adjNode) => {
        if (!visited.has(adjNode)) {
            this.dfs(adjNode, visited, group, map);
        }
    });
}

findAllGroups(map: Map<any, any[]>) {
    const visited = new Set<any>();
    const groups: any[] = [];

    // Iterate over each node in the map.
    map.forEach((value, key) => {
      // If the node has not been visited, start a new group.
      if (!visited.has(key)) {
        const group: any[] = [];
        this.dfs(key, visited, group, map);
        groups.push(group);
      }
    })
    return groups;
}

  handleCollisions() {
    const allClusters: any[] = [];
    Object.entries(this.cfClusters).map((curr) => {
      allClusters.push(curr[1]);
    });
    Object.entries(this.afClusters).map((curr) => {
      allClusters.push(curr[1]);
    });
    Object.entries(this.xcipioClusters).map((curr) => {
      allClusters.push(curr[1]);
    });
    Object.entries(this.ccpagClusters).map((curr) => {
      allClusters.push(curr[1]);
    });
    Object.entries(this.mdf2Clusters).map((curr) => {
      allClusters.push(curr[1]);
    });
    Object.entries(this.mdf3Clusters).map((curr) => {
      allClusters.push(curr[1]);
    });
    Object.entries(this.admfClusters).map((curr) => {
      allClusters.push(curr[1]);
    });
    const collisionMap = new Map<any, any[]>();
    allClusters.map((ne) => {
      let layer: any;
      if (ne instanceof L.MarkerCluster) {
        layer = ne as L.MarkerCluster;
      } else {
        layer = ne as L.Marker;
      }
      let layerLoc = layer.getLatLng();
      if (layer.options.isUpdatedLoc) {
        layerLoc = layer.options.originalLoc;
      }
      allClusters.map((ne2) => {
        if (ne._leaflet_id !== ne2._leaflet_id) {
          let layer2: any;
          if (ne2 instanceof L.MarkerCluster) {
            layer2 = ne2 as L.MarkerCluster;
          } else {
            layer2 = ne2 as L.Marker
          }
          let layer2Loc = layer2.getLatLng();
          if (layer2.options.isUpdatedLoc) {
            layer2Loc = layer2.options.originalLoc;
          }
          const layer2LocPoint = this.map.latLngToContainerPoint(layer2Loc);
          const layerLocPoint = this.map.latLngToContainerPoint(layerLoc);
          const dist = layerLocPoint.distanceTo(layer2LocPoint);
          if (dist < 50) {
            if (collisionMap.has(ne)) {
              collisionMap.get(ne)?.push(ne2);
            } else {
              collisionMap.set(ne, [ne2]);
            }
          }
        }
      });
    });
    // Find and output all groups.
    const groups = this.findAllGroups(collisionMap);
    this.displayGrid(groups);
  }

  getGridPlacement = (
    center: L.LatLng,
    totalCount: number,
  ) => {
    const gridPositions = [];
    const gridSize = Math.ceil(Math.sqrt(totalCount));
    const centerPoint = this.map.latLngToContainerPoint(center);
    const startX = centerPoint.x - ((30 * gridSize) / 2);
    const startY = centerPoint.y - ((30 * gridSize) / 2);
      for (let row = 0; row < gridSize; row += 1) {
        for (let col = 0; col < gridSize; col += 1) {
          const posX = (startX + col * 50);
          const posY = (startY + row * 50) - (col * 20);
          const position = this.map.containerPointToLatLng({ x: posX, y: posY } as L.PointExpression);
          gridPositions.push(position);
        }
      }

    return gridPositions;
  };

  private displayGrid(groups: any[]) {
    groups.forEach((group: []) => {
      const latLngs: LatLng[] = [];
      group.forEach((ne: any) => {
        let layer: any;
        if (ne instanceof L.MarkerCluster) {
          layer = ne as L.MarkerCluster;
        } else {
          layer = ne as L.Marker;
        }
        let layerLoc = layer.getLatLng();
        if (layer.options.isUpdatedLoc) {
          layerLoc = layer.options.originalLoc;
        }
        latLngs.push(layerLoc);
      });
      const bounds = L.latLngBounds([...latLngs]);
      const center = bounds.getCenter();
      if (center) {
        const gridPositions = this.getGridPlacement(center, group.length);
        group.forEach((ne: any, index: number) => {
          const position = gridPositions[index];
          let layer: any;
          if (ne instanceof L.MarkerCluster) {
            layer = ne as L.MarkerCluster;
          } else {
            layer = ne as L.Marker;
          }
          if (!layer.options.isUpdatedLoc) {
            layer.options.isUpdatedLoc = true;
            layer.options.originalLoc = layer.getLatLng();
          }
          layer.setLatLng(position);
        });
      }
    });
  }

  addDistinctDestinationsFromCluster(cluster: MarkerCluster, details: any, distinctDest: DistinctDestination, sourceType: NetworkTopologyClusterElementsEnum) {
    const connectionsByType = details.connections;
    Object.keys(connectionsByType).forEach((key) => {
      const connections = connectionsByType[key] as [];
      connections.forEach((con: Connection) => {
      if (distinctDest[con.destinationId]) {
        distinctDest[con.destinationId].statuses.push(con.status)
      } else {
        const destLoc = this.getMarkerLocationByNeId(con.destinationId, con.destType);
        if (destLoc) {
          let updatedToDict = false;
          Object.keys(distinctDest).forEach((key) => {
            const value = distinctDest[key];
            if (destLoc === value.destinationLocation) {
              distinctDest[key].statuses.push(con.status);
              updatedToDict = true;
            }
          })
          if (!updatedToDict) {
            distinctDest[con.destinationId] = {
                    sourceLeafletId: (cluster as any)._leaflet_id,
                    sourceId: details.data.id,
                    sourceType,
                    destinationType: this.getMarkerByLeafletId(this.getLeafletId(con.destinationId)).markerType,
                    destinationLocation: this.getMarkerLocationByNeId(con.destinationId, con.destType),
                    statuses: [con.status]
                  };
          }
        }
      }
      })
    })
  }

  removeConnectionsToSelf(distinctDestinations: DistinctDestination) {
    return Object.keys(distinctDestinations).reduce((acc, curr) => {
      if (this.getLeafletId(curr) !== distinctDestinations[curr].sourceLeafletId) {
        acc[curr] = distinctDestinations[curr];
      }
      return acc;
    }, {} as DistinctDestination);
  }

  getEdgesForNonClusteredMarker(details: any, sourceLeafletId: string, sourceType: NetworkTopologyElementsEnum): NeEdge[] {
    const ret: NeEdge[] = [];
    const connectionsByType = details.connections;
    Object.keys(connectionsByType).forEach((key) => {
      const connections = connectionsByType[key] as [];
      const groupByLeafletId: {[leafletId: string]: Connection[]} = connections.reduce((acc, con: Connection) => {
        const leafletId = this.getLeafletId(con.destinationId);
        if (leafletId) {
          if (!acc[leafletId]) {
            acc[leafletId] = [con];
          } else {
            acc[leafletId].push(con);
          }
        }

        return acc;
      }, {} as any);

      Object.entries(groupByLeafletId).forEach((entry) => {
        const statuses = entry[1].map((con: Connection) => con.status);
        const edge: NeEdge = {
          edgeKey: '',
          sourceLeafletId,
          sourceLocation: this.getMarkerLocationByNeId(details.data.id),
          sourceId: details.data.id,
          sourceType,
          destinationId: '',
          destinationLeafletId: entry[0],
          destinationType: this.getMarkerByLeafletId(entry?.[0])?.markerType,
          destinationLocation: this.getMarkerLocationByLeafletId(entry[0]),
          statuses,
          status: getClusterStatus2(statuses)
        };
        const tooltipData = getTooltipData(edge, this);
        ret.push({ ...edge, tooltipData });
      });
      // connections.forEach((con: Connection) => {
      //   const edge: NeEdge = {
      //     edgeKey: '',
      //     sourceLeafletId,
      //     sourceLocation: this.getMarkerLocationByNeId(details.data.id),
      //     sourceId: details.data.id,
      //     sourceType,
      //     destinationId: con.destinationId,
      //     destinationLeafletId: this.getLeafletId(con.destinationId),
      //     destinationType: this.getMarkerByLeafletId(this.getLeafletId(con.destinationId)).markerType,
      //     destinationLocation: this.getMarkerLocationByLeafletId(con.destinationId),
      //     status: con.status
      //   };
      //   let statuses = [];
      //   if (this.isClusterType(edge.destinationType)) {
      //   }
      //   const tooltipData = getTooltipData(edge, this);
      //   ret.push({ ...edge, tooltipData });
      // });
    });
    return ret;
  }

  getLeafletId(networkElementId: string, type ?: 'af' | 'cf' | 'xcipio') {
    if (type === 'af') {
      return this.afIdToLeafletIdMap[networkElementId].leafletId;
    }
    if (type === 'cf') {
      return this.cfIdToLeafletIdMap[networkElementId].leafletId;
    }
    if (type === 'xcipio') {
      return this.xcipioIdToLeafletIdMap[networkElementId].leafletId || this.ccpagIdToLeafletIdMap[networkElementId].leafletId
      || this.mdf2IdToLeafletIdMap[networkElementId].leafletId || this.mdf3IdToLeafletIdMap[networkElementId].leafletId || this.admfIdToLeafletIdMap[networkElementId].leafletId;
    }

    return this.afIdToLeafletIdMap[networkElementId]?.leafletId ||
    this.cfIdToLeafletIdMap[networkElementId]?.leafletId ||
    this.xcipioIdToLeafletIdMap[networkElementId]?.leafletId ||
    this.ccpagIdToLeafletIdMap[networkElementId]?.leafletId ||
    this.mdf2IdToLeafletIdMap[networkElementId]?.leafletId ||
    this.mdf3IdToLeafletIdMap[networkElementId]?.leafletId ||
    this.admfIdToLeafletIdMap[networkElementId]?.leafletId;
  }

  getEdges(nodeId: string) {
    const outEdges = this.graph.outEdges(nodeId);
    return outEdges;
  }

  getAllConnections() {
    const ret: NeEdge[] = [];
    const itr = this.graph.edgeEntries();
    let result = itr.next();
    while (!result.done) {
      const { attributes } = result.value;
      ret.push({ ...attributes, edgeKey: result.value.edge } as NeEdge);
      result = itr.next();
    }
    return ret;
  }

  getAllPathsFromNodeToLeaf(nodeId: string, mode: 'in' | 'out' = 'out') {
    const ret: NeEdge[] = [];
    const markerSummary: MarkerSummary = this.getMarkerByLeafletId(nodeId);
    let depthMax = 0;
    let connectionDir: string[][] = [[]];

    if (markerSummary.markerType === NetworkTopologyClusterElementsEnum.AccessFunctionCluster || markerSummary.markerType === NetworkTopologyElementsEnum.AccessFunction) {
      depthMax = 2;
      connectionDir = [['in', 'out'], ['out']];
    }

    if (markerSummary.markerType === NetworkTopologyClusterElementsEnum.XcipioCluster || markerSummary.markerType === NetworkTopologyElementsEnum.Xcipio) {
      depthMax = 2;
      connectionDir = [['in', 'out'], ['out']];
    }

    if (markerSummary.markerType === NetworkTopologyClusterElementsEnum.CollectionFunctionCluster || markerSummary.markerType === NetworkTopologyElementsEnum.CollectionFunction) {
      depthMax = 2;
      connectionDir = [['in'], ['in']];
    }

    if (markerSummary.markerType === NetworkTopologyClusterElementsEnum.CCPAGCluster || markerSummary.markerType === NetworkTopologyElementsEnum.CCPAG) {
      depthMax = 2;
      connectionDir = [['in', 'out'], ['out']];
      let edges = this.doBfsForCCPAG(nodeId, mode, depthMax, connectionDir);
      edges = this.filterDuplicateEdges(edges)
      return edges;
    }

    if (markerSummary.markerType === NetworkTopologyClusterElementsEnum.ADMFCluster || markerSummary.markerType === NetworkTopologyElementsEnum.ADMF) {
      depthMax = 2;
      connectionDir = [['in', 'out'], ['out']];
    }

    // MDF2 or MDF3
    if (markerSummary.markerType === NetworkTopologyElementsEnum.MDF2 || markerSummary.markerType === NetworkTopologyClusterElementsEnum.MDF2Cluster || markerSummary.markerType === NetworkTopologyElementsEnum.MDF3 || markerSummary.markerType === NetworkTopologyClusterElementsEnum.MDF3Cluster) {
      if (markerSummary.markerType === NetworkTopologyElementsEnum.MDF2 || markerSummary.markerType === NetworkTopologyClusterElementsEnum.MDF2Cluster) {
        depthMax = 1;
        connectionDir = [['in', 'out']];
      } else if (markerSummary.markerType === NetworkTopologyElementsEnum.MDF3 || markerSummary.markerType === NetworkTopologyClusterElementsEnum.MDF3Cluster) {
        depthMax = 2;
        connectionDir = [['in', 'out'], ['in']];
        mode = 'in';
      }
    }

    const edges = this.doBfsForGeneralCase(nodeId, mode, depthMax, connectionDir);
    return this.filterDuplicateEdges(edges);
  }

  filterDuplicateEdges = (edges: NeEdge[]) => {
    const visitedEdgeKeys: {[edgeKey: string]: boolean} = {};
    return edges.filter((x) => {
      if (!visitedEdgeKeys[x.edgeKey]) {
        visitedEdgeKeys[x.edgeKey] = true;
        return true;
      }
      return false;
    })
  }

  doBfsForCCPAG(nodeId: string, mode: 'in'| 'out', depthMax: number, connectionDir: string[][]) {
    const ret:NeEdge[] = [];
    bfsFromNode(this.graph, nodeId, (node, attr, depth) => {
      if (depth < depthMax) {
        connectionDir[depth].forEach((dir) => {
          if (dir === 'out') {
            this.graph.findOutboundEdge(node, (edgeKey: string, edgeAttr) => {
              if (edgeAttr.destinationType !== NetworkTopologyClusterElementsEnum.AccessFunctionCluster && edgeAttr.destinationType !== NetworkTopologyElementsEnum.AccessFunction) {
                ret.push({ ...edgeAttr as NeEdge, edgeKey });
              }
            })
          } else {
            this.graph.findInboundEdge(node, (edgeKey: string, edgeAttr) => {
              ret.push({ ...edgeAttr as NeEdge, edgeKey });
            })
          }
        });
      }
    }, {
      mode
    });
    return ret;
  }

  doBfsForGeneralCase(nodeId: string, mode: 'in'| 'out', depthMax: number, connectionDir: string[][]) {
    const ret:NeEdge[] = [];
    bfsFromNode(this.graph, nodeId, (node, attr, depth) => {
      if (depth < depthMax) {
        connectionDir[depth].forEach((dir) => {
          if (dir === 'out') {
            this.graph.findOutboundEdge(node, (edgeKey: string, edgeAttr) => {
              ret.push({ ...edgeAttr as NeEdge, edgeKey });
            })
          } else {
            this.graph.findInboundEdge(node, (edgeKey: string, edgeAttr) => {
              ret.push({ ...edgeAttr as NeEdge, edgeKey });
            })
          }
        });
      }
    }, {
      mode
    });
    return ret;
  }

  getMarkerByLeafletId(leafletId: string): MarkerSummary {
    return this.afLeafletIdToMarkerMap[leafletId] ||
      this.cfLeafletIdToMarkerMap[leafletId] ||
      this.xcipioLeafletIdToMarkerMap[leafletId] ||
      this.ccpagLeafletIdToMarkerMap[leafletId] ||
      this.mdf2LeafletIdToMarkerMap[leafletId] ||
      this.mdf3LeafletIdToMarkerMap[leafletId] ||
      this.admfLeafletIdToMarkerMap[leafletId];
  }

  getChildDataByLeafletId(leafletId: string): NetworkTopologyElement[] {
    const ret = this.afLeafletIdToMarkerMap[leafletId] ||
      this.cfLeafletIdToMarkerMap[leafletId] ||
      this.xcipioLeafletIdToMarkerMap[leafletId] ||
      this.ccpagLeafletIdToMarkerMap[leafletId] ||
      this.mdf2LeafletIdToMarkerMap[leafletId] ||
      this.mdf3LeafletIdToMarkerMap[leafletId] ||
      this.admfLeafletIdToMarkerMap[leafletId];
    return ret.childNeData || [];
  }

  getMarkerLocationByLeafletId(leafletId: string): LatLng {
    const marker = this.getMarkerByLeafletId(leafletId);
    return marker.location;
  }

  getMarkerLocationByNeId(neId: string, destType ?: NetworkTopologyElementsEnum | NetworkTopologyClusterElementsEnum): LatLng {
    if (destType === NetworkTopologyClusterElementsEnum.XcipioCluster || destType === NetworkTopologyElementsEnum.Xcipio) {
      return this.xcipioIdToLeafletIdMap[neId]?.location || this.ccpagIdToLeafletIdMap[neId]?.location || this.mdf2IdToLeafletIdMap[neId]?.location || this.mdf3IdToLeafletIdMap[neId]?.location || this.admfIdToLeafletIdMap[neId]?.location;
    }
    if (destType === NetworkTopologyClusterElementsEnum.AccessFunctionCluster || destType === NetworkTopologyElementsEnum.AccessFunction) {
      return this.afIdToLeafletIdMap[neId]?.location;
    }
    if (destType === NetworkTopologyClusterElementsEnum.CollectionFunctionCluster || destType === NetworkTopologyElementsEnum.CollectionFunction) {
      return this.cfIdToLeafletIdMap[neId]?.location;
    }
    // if (destType === NetworkTopologyClusterElementsEnum.CCPAGCluster || destType === NetworkTopologyElementsEnum.CCPAG) {
    //   return this.ccpagIdToLeafletIdMap[neId]?.location;
    // }
    return this.xcipioIdToLeafletIdMap[neId]?.location || this.afIdToLeafletIdMap[neId]?.location || this.cfIdToLeafletIdMap[neId]?.location || this.ccpagIdToLeafletIdMap[neId]?.location || this.mdf2IdToLeafletIdMap[neId]?.location || this.mdf3IdToLeafletIdMap[neId]?.location || this.admfIdToLeafletIdMap[neId]?.location;
  }

  isClusterType(type: NetworkTopologyClusterElementsEnum | NetworkTopologyElementsEnum) {
    return (
      type === NetworkTopologyClusterElementsEnum.CollectionFunctionCluster ||
      type === NetworkTopologyClusterElementsEnum.AccessFunctionCluster ||
      type === NetworkTopologyClusterElementsEnum.XcipioCluster ||
      type === NetworkTopologyClusterElementsEnum.MDF2Cluster ||
      type === NetworkTopologyClusterElementsEnum.MDF3Cluster ||
      type === NetworkTopologyClusterElementsEnum.ADMFCluster);
  }

}
